import React from 'react';
import { ShieldCheckIcon, LockClosedIcon, EyeIcon, DocumentTextIcon, ArrowLeftIcon, GlobeAltIcon, EnvelopeIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';

interface PrivacyPolicyProps {
  onBack: () => void;
}

const Section = ({ title, icon: Icon, children }: { title: string; icon: React.ComponentType<{ className?: string }>; children: React.ReactNode }) => (
  <section className="mb-12">
    <div className="mb-4 flex items-center gap-3">
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-2 text-slate-600">
        <Icon className="h-5 w-5" />
      </div>
      <h2 className="text-xl font-bold text-slate-900">{title}</h2>
    </div>
    <div className="space-y-4 pl-12 leading-relaxed text-slate-600">
      {children}
    </div>
  </section>
);

export function PrivacyPolicy({ onBack }: PrivacyPolicyProps) {
  const lastUpdated = '31 de Marco de 2026';

  return (
    <div className="min-h-screen bg-slate-50 selection:bg-blue-100">
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/96 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-6">
          <button onClick={onBack} className="flex items-center gap-2 text-sm font-medium text-slate-500 transition-colors hover:text-slate-900">
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </button>
          <div className="flex items-center gap-2">
            <ShieldCheckIcon className="h-5 w-5 text-blue-600" />
            <span className="font-bold text-slate-900">Privacidade AQL</span>
          </div>
          <div className="w-16" />
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-16">
        <div className="mb-16 text-center">
          <h1 className="mb-4 text-4xl font-black tracking-tight text-slate-900">Politica de Privacidade</h1>
          <p className="font-medium text-slate-500">Ultima atualizacao: {lastUpdated}</p>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm md:p-12">
          <p className="mb-12 border-l-4 border-blue-500 pl-6 text-lg italic text-slate-700">
            Na AQL Engineering levamos a serio a seguranca dos seus dados industriais, operacionais e clinicos.
            Esta politica explica como recolhemos, utilizamos, protegemos e conservamos a sua informacao nas nossas aplicacoes.
          </p>

          <Section title="Recolha de Dados" icon={FileText}>
            <p>Recolhemos apenas a informacao necessaria para operar a plataforma e prestar suporte tecnico e funcional.</p>
            <ul className="list-disc space-y-2 pl-5">
              <li><strong>Identificacao:</strong> nome, email profissional e perfil funcional para autenticacao, auditoria e controlo de acessos.</li>
              <li><strong>Dados operacionais:</strong> alertas de visao, classificacoes humanas, sensores, eventos de dispositivos e historico de utilizacao.</li>
              <li><strong>Dados clinicos ou laboratoriais:</strong> apenas quando necessarios ao contexto de validacao e sempre com controlo de acesso adequado.</li>
            </ul>
          </Section>

          <Section title="Uso da Informacao" icon={Eye}>
            <p>Os dados sao utilizados exclusivamente para fins legitimos de operacao, seguranca e melhoria do servico.</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>Apresentar dashboards, alertas e fluxos de decisao em tempo real.</li>
              <li>Permitir validacao humana, rastreabilidade e auditoria de eventos.</li>
              <li>Melhorar a qualidade dos modelos de IA com base em sinais anonimizados e controlados.</li>
              <li>Prestar suporte tecnico e responder a incidentes de seguranca ou disponibilidade.</li>
            </ul>
          </Section>

          <Section title="Seguranca e Armazenamento" icon={Lock}>
            <p>Adotamos medidas tecnicas e organizativas adequadas para proteger a informacao processada.</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>Autenticacao com tokens de sessao e mecanismos modernos de identidade.</li>
              <li>Separacao entre dados operacionais de curto prazo e armazenamento persistente de longo prazo.</li>
              <li>Restricao de acesso por perfil, funcao e contexto de operacao.</li>
            </ul>
          </Section>

          <Section title="Partilha de Dados" icon={Globe}>
            <p>A AQL Engineering nao vende os seus dados nem os partilha para fins publicitarios.</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>Partilhamos apenas com profissionais autorizados dentro do contexto operacional da mesma organizacao.</li>
              <li>Pode existir partilha quando exigida por obrigacao legal, auditoria ou cumprimento regulamentar.</li>
            </ul>
          </Section>

          <Section title="Cookies e Armazenamento Local" icon={Settings2}>
            <p>Utilizamos cookies tecnicos e armazenamento local para manter a plataforma funcional e coerente com as suas preferencias.</p>
            <ul className="list-disc space-y-2 pl-5">
              <li><strong>Essenciais:</strong> sessao autenticada, seguranca minima, preferencia de tema e estado necessario da interface.</li>
              <li><strong>Preferencias:</strong> idioma, filtros, contexto operacional e escolhas persistentes do utilizador.</li>
              <li><strong>Melhoria do produto:</strong> apenas quando consentido, para recolher sinais anonimizados de uso e melhorar a experiencia.</li>
            </ul>
            <p>Pode gerir estas preferencias a qualquer momento no menu de conta da aplicacao.</p>
          </Section>

          <Section title="Os Seus Direitos" icon={ShieldCheckIcon}>
            <p>De acordo com o RGPD, pode solicitar acesso, retificacao, limitacao ou eliminacao dos seus dados, nos termos legais aplicaveis.</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>Aceder a informacao mantida sobre a sua utilizacao da plataforma.</li>
              <li>Corrigir dados de perfil desatualizados ou incompletos.</li>
              <li>Pedir esclarecimentos sobre conservacao, origem ou finalidade do tratamento.</li>
            </ul>
          </Section>

          <div className="mt-16 flex flex-col items-center border-t border-slate-100 pt-12">
            <p className="mb-4 font-bold text-slate-900">Tem questoes sobre privacidade ou dados pessoais?</p>
            <a
              href="mailto:privacy@aqlengineering.com"
              className="flex items-center gap-2 rounded-2xl bg-slate-900 px-6 py-3 text-sm font-bold text-white transition-all hover:bg-blue-600"
            >
              <Mail className="h-4 w-4" />
              Contactar privacidade
            </a>
          </div>
        </div>
      </main>

      <footer className="mx-auto max-w-4xl px-6 py-12 text-center text-xs font-bold uppercase tracking-widest text-slate-400">
        AQL Engineering Systems • 2026 • Made in Portugal
      </footer>
    </div>
  );
}
